/**
 * Function to map classNames to the cssModule classnames provided by the bundler
 *
 * @param { array || string } classes list of classes to map
 * @param { object } cssModule css module to perform lookups on
 * @param { object } override Consumer override information for classname generation
 * @param { string } override.className Base block name to use
 * @param { boolean } override.replaceBaseClass Flag to add or replace internal block name
 * @param { object } override.cssModule Consumer CSS Module for classname resolution
 * @param { object } override.retainMissingModuleClasses Flag to not remove css classnames missing from the provided CSS Mod
 *
 * @returns { string } css classes converted to those expected by the cssModule
 */
function convertToModuleClasses(classes, cssModule, override) {
  // Get the override class or classes for possible passthrough
  let overrideClass = override;
  if (override && typeof override === 'object') {
    overrideClass = override.className;
  }
  overrideClass = overrideClass?.split(' ');
  const overrideModule = override?.cssModule;
  let moduleClassNames = '';
  if (Array.isArray(classes)) {
    moduleClassNames = [];
    classes.forEach(classname => {
      // If the classname exists in the internal css module add it
      if (cssModule[classname]) {
        moduleClassNames.push(cssModule[classname]);
      }

      // If the classname exists in the override module add it
      if (overrideModule && overrideModule[classname]) {
        moduleClassNames.push(overrideModule[classname]);
      } else if (overrideModule && override.retainMissingModuleClasses || !overrideModule) {
        // Otherwise if the allow missing module classes is flagged or we have no css module
        if (overrideClass?.find(oClass => classname.indexOf(oClass) === 0)) {
          // Check if we have a match for the override class and let that passthrough
          moduleClassNames.push(classname);
        }
      }
    });
  } else {
    const cssClasses = classes.split(' ');
    cssClasses.forEach(classname => {
      // If the classname exists in the internal css module add it
      if (cssModule[classname]) {
        moduleClassNames += `${cssModule[classname]} `;
      }

      // If the classname exists in the override module add it
      if (overrideModule && overrideModule[classname]) {
        moduleClassNames += `${overrideModule[classname]} `;
      } else if (overrideModule && override.retainMissingModuleClasses || !overrideModule) {
        // Otherwise if the allow missing module classes is flagged or we have no css module
        if (overrideClass?.find(oClass => classname.indexOf(oClass) === 0)) {
          // Check if we have a match for the override class and let that passthrough
          moduleClassNames += `${classname} `;
        }
      }
    });
  }
  return moduleClassNames;
}

/**
 * Lookup the original className name from cssModule, provides support for consumer developers to
 * either provide the className as as original string e.g. 'MyClass' or as a translated cssModule
 * name e.g. 'MyClass_xyz'.
 *
 * @param { string } className list of classes to map
 * @param { object } cssModule css module to perform lookups on
 * @param { object } retainMissingModuleClasses Flag to not remove css classnames missing from the provided CSS Mod
 *
 * @returns { string } original block name that doesn't include the encoding from the cssModule
 */
function resolveBlockFromCssModule(_ref) {
  let {
    className,
    cssModule,
    retainMissingModuleClasses
  } = _ref;
  if (cssModule) {
    // Class name has been provided in its original format
    if (cssModule[className]) {
      return className;
    }
    const found = Object.keys(cssModule).find(key => cssModule[key] === className);
    if (!found && retainMissingModuleClasses) {
      return className;
    }
    return found || '';
  }
  return className;
}

/**
 * Generates a BEM compatible classname while allowing override from consumer to add to or replace the
 * internally used block names with their own (and removing our CSS from use).
 *
 * @param { object } bem BEM class information
 * @param { string } bem.block Block portion of the BEM identifier
 * @param { string } bem.element Element portion of the BEM identifier
 * @param { string } bem.modifiers Modifiers to the block portion of the BEM identifier
 * @param { object } bem.override Consumer override information for classname generation
 * @param { string } bem.override.className Base block name to use
 * @param { boolean } bem.override.replaceBaseClass Flag to add or replace internal block name
 * @param { object } bem.override.cssModule Consumer CSS Module for classname resolution
 * @param { object } bem.override.retainMissingModuleClasses Flag to not remove css classnames missing from the provided CSS Module
 * @param { object } cssModule Internal CSS Module
 *
 * @returns { string } CSS classes to use as an elements classname property
 */
export default function generateClasses(_ref2, cssModule) {
  let {
    block: rootBlock,
    element,
    modifiers,
    override
  } = _ref2;
  let classnames = '';
  let blocks;

  // Check if the override class is set as a replace rather than an addition
  if (typeof override === 'object' && override.replaceBaseClass) {
    blocks = [resolveBlockFromCssModule(override)];
  } else {
    blocks = [rootBlock];
    if (override) {
      if (typeof override !== 'object') {
        blocks.push(override);
      } else {
        blocks.push(resolveBlockFromCssModule(override));
      }
    }
  }

  // Check if any of the blocks is a string of classes, this can happen with nested elements
  blocks = Array.from(blocks, item => {
    return item.split(' ');
  }).flat(); // Flatten any array of array created into a single end array of items

  blocks.forEach(block => {
    if (block) {
      const base = `${block}${element ? `__${element}` : ''}`;
      classnames = `${classnames} ${base}`;
      if (modifiers) {
        Object.keys(modifiers).forEach(modifier => {
          const enabled = modifiers[modifier];
          if (enabled) {
            classnames = `${classnames} ${base}--${modifier}`;
          }
        });
      }
    }
  });
  if (cssModule) {
    classnames = convertToModuleClasses(classnames, cssModule, override);
  }
  return classnames.trim();
}

/**
 * Generate the name for a sub block which is related to the higher level block
 * but distinct enough to have its own class namespace.
 *
 * @param { object } bem BEM class information
 * @param { string } bem.block Block portion of the BEM identifier
 * @param { string } bem.sub Additional portion of name to append to the block to create a new block identifier
 * @param { object } bem.override Consumer override information for classname generation
 * @param { string } bem.override.className Base block name to use
 * @param { boolean } bem.override.replaceBaseClass Flag to add or replace internal block name
 * @param { object } bem.override.cssModule Consumer CSS Module for classname resolution
 * @param { object } bem.override.retainMissingModuleClasses Flag to not remove css classnames missing from the provided CSS Module
 * @param { object } cssModule Internal CSS Module
 *
 * @returns { string } CSS classes to use as an elements classname property
 */
export function generateNewSubBlock(_ref3, cssModule) {
  let {
    block,
    sub,
    override
  } = _ref3;
  const classes = generateClasses({
    block,
    override
  }); // Let the standard process handle the override logic
  const split = classes.split(' ');
  let classname = '';
  split.forEach(newBlock => {
    const base = `${newBlock}${sub}`;
    classname = `${classname} ${base}`;
  });
  if (cssModule) {
    classname = convertToModuleClasses(classname, cssModule, override);
  }
  return classname.trim();
}

/**
 * Generate a modifier class to apply to an element via direct DOM manipulation.
 *
 * @param { object } bem BEM class information
 * @param { string } bem.block Block portion of the BEM identifier
 * @param { string } bem.element Element portion of the BEM identifier
 * @param { string } bem.modifiers Modifiers to the block portion of the BEM identifier
 * @param { object } bem.override Consumer override information for classname generation
 * @param { string } bem.override.className Base block name to use
 * @param { boolean } bem.override.replaceBaseClass Flag to add or replace internal block name
 * @param { object } bem.override.cssModule Consumer CSS Module for classname resolution
 * @param { object } bem.override.retainMissingModuleClasses Flag to not remove css classnames missing from the provided CSS Module
 * @param { object } cssModule Internal CSS Module
 *
 * @returns { string } CSS classes to use as an elements classname property
 */
export function generateModifierClass(_ref4, cssModule) {
  let {
    block,
    element,
    modifier,
    override
  } = _ref4;
  const modifiers = {};
  modifiers[modifier] = true;
  let classes = generateClasses({
    block,
    element,
    modifiers,
    override
  }).split(' ');
  classes = classes.filter(item => item.includes('--'));
  if (cssModule) {
    classes = convertToModuleClasses(classes, cssModule, override);
  }
  return classes.filter(n => n); // Remove any empty elements
}

/**
 * Generate transition modifier classes for usages of 'react-transition-group'
 *
 * @param { object } bem BEM class information
 * @param { string } bem.block Block portion of the BEM identifier
 * @param { string } bem.element Element portion of the BEM identifier
 * @param { object } bem.override Consumer override information for classname generation
 * @param { string } bem.override.className Base block name to use
 * @param { boolean } bem.override.replaceBaseClass Flag to add or replace internal block name
 * @param { object } bem.override.cssModule Consumer CSS Module for classname resolution
 * @param { object } bem.override.retainMissingModuleClasses Flag to not remove css classnames missing from the provided CSS Module
 * @param { object } cssModule Internal CSS Module
 *
 * @returns { object } CSS classes to use as Transition's classNames property
 */
export function generateCSSTransitionClasses(_ref5, cssModule) {
  let {
    block,
    element,
    override
  } = _ref5;
  const classes = generateClasses({
    block,
    element,
    override
  });
  const split = classes.split(' ');
  const createClass = modifier => {
    let transitionClasses = split.map(classname => `${classname}${modifier}`).join(' ');
    if (cssModule) {
      transitionClasses = convertToModuleClasses(transitionClasses, cssModule, override);
    }
    return transitionClasses.trim();
  };
  return {
    appear: createClass('--appear') || undefined,
    appearActive: createClass('--appear-active') || undefined,
    appearDone: createClass('--appear-done') || undefined,
    enter: createClass('--enter') || undefined,
    enterActive: createClass('--enter-active') || undefined,
    enterDone: createClass('--enter-done') || undefined,
    exit: createClass('--exit') || undefined,
    exitActive: createClass('--exit-active') || undefined,
    exitDone: createClass('--exit-done') || undefined
  };
}